import DsAppIcon from "../components/ds/AppIcon";
import CardMedia from "../components/discover/CardMedia";
import Button from "../components/ds/Button";
import Icon from "../components/ds/Icon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuthModal, useGuardedNavigate } from "../context/AuthModalContext";
import { FiDownload, FiGift, FiLoader, FiTag, FiX } from "react-icons/fi";
import { downloadProductConsultingRecording } from "../api/mintSpaceConsulting";
import { useTranslation } from "../context/TranslationContext";
import { useAuth } from "../context/AuthContext";
import { getMintSpaceById, getMintSpaceProduct, joinMintSpace } from "../api/mintSpace";
import useCheckout from "../hooks/useCheckout";
import { quotePromoCode } from "../api/checkout";
import { discountLabel, money as promoMoney, promoErrorText } from "../utils/promoCodes";
import CheckoutModal from "../components/CheckoutModal";
import { getMintSpaceAppStoreCatalog } from "../data/mintSpaceAppStoreCatalog";
import {
  resolveAppCatalogEntry,
  isProductFreeForJoin,
  instanceNavId,
  normalizeInstalledAppId,
  normalizeInstalledApps,
  CONSULTING_APP_ID,
} from "../utils/mintSpaceApps";
import ProductReviews from "../components/ProductReviews";
import JoinRequestModal from "../components/JoinRequestModal";
import PageTransition from "../components/motion/PageTransition";
import UserProfileLink from "../components/UserProfileLink";
import ProductAppsFolder from "../components/ProductAppsFolder";

// GEL is the only supported currency for now.
const currencySymbol = () => "₾";
const PERIOD_WORD = {
  "1w": "week",
  "1m": "month",
  "3m": "3 months",
  "6m": "6 months",
  "1y": "year",
};

/**
 * Strips obviously dangerous markup from the owner-authored description HTML before it is
 * injected. The description is written by the space owner in the pricing editor's rich-text
 * field; this is a lightweight guard, not a full sanitizer.
 */
const sanitizeHtml = (html) =>
  String(html || "")
    .replace(/<\s*(script|style|iframe|object|embed)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, "")
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, "")
    .replace(/\son\w+\s*=\s*'[^']*'/gi, "")
    .replace(/javascript:/gi, "");

/**
 * Buyer-facing product details page. Reads the saved app pricing config from a Mint Space and
 * renders the in-page media (image or video), title, description and a checkout card.
 *
 * Route: /mint-space/:spaceId/product/:appId?  (appId optional — falls back to the first product)
 */
// `embedded` renders the page inside the Mint Space window (sidebar products): route params
// are replaced by props and the standalone chrome (full-height bg, top bar) is dropped.
const MintSpaceProductDetailsPage = ({
  isDark = true,
  setToast,
  spaceId: spaceIdProp,
  productId: productIdProp,
  embedded = false,
  mintSpace: mintSpaceProp,
  membership: membershipProp,
  onJoinSuccess,
  onOpenApp,
}) => {
  const { t } = useTranslation();
  const navigate = useGuardedNavigate();
  const { openAuthModal } = useAuthModal();
  const { isAuthenticated } = useAuth();
  const params = useParams();
  const spaceId = spaceIdProp || params.spaceId;
  const productId = productIdProp || params.productId;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [membership, setMembership] = useState(membershipProp || null);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joining, setJoining] = useState(false);
  // Promo code: what's typed, and — once the server has accepted it — the price with it.
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoError, setPromoError] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);

  /**
   * Re-fetch the product. `silent` refreshes in place — used after joining or paying,
   * where swapping the page for a spinner would unmount everything rendered below it,
   * open modals included. A failed silent refresh keeps what's on screen.
   */
  const loadProduct = useCallback(async ({ silent = false } = {}) => {
    if (!spaceId) return;
    try {
      if (!silent) setLoading(true);
      const res = await getMintSpaceProduct(spaceId, productId);
      if (res?.success) {
        setData(res.data);
        if (res.data.membership) {
          setMembership(res.data.membership);
        }
      } else if (!silent) {
        setError(true);
        setErrMsg(res?.message || "");
      }
    } catch (e) {
      if (!silent) {
        setError(true);
        setErrMsg(e?.message || "");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [spaceId, productId]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!spaceId) {
        if (active) setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const res = await getMintSpaceProduct(spaceId, productId);
        if (!active) return;
        if (res?.success) {
          setData(res.data);
          if (res.data.membership) {
            setMembership(res.data.membership);
          }
        } else {
          setError(true);
          setErrMsg(res?.message || "");
        }
      } catch (e) {
        if (active) {
          setError(true);
          setErrMsg(e?.message || "");
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [spaceId, productId]);

  useEffect(() => {
    if (membershipProp) {
      setMembership(membershipProp);
    }
  }, [membershipProp]);

  // A code was checked against one product; another product starts without it.
  useEffect(() => {
    setPromoOpen(false);
    setPromoInput("");
    setPromoError("");
    setAppliedPromo(null);
  }, [spaceId, productId]);

  const product = data?.product || null;
  const space = data?.space;

  const catalogName = useMemo(() => {
    if (!product) return "";
    return resolveAppCatalogEntry(getMintSpaceAppStoreCatalog(t), product.appId)?.name || "";
  }, [product, t]);

  const appName = useMemo(() => {
    if (!product?.appId) return catalogName;
    return resolveAppCatalogEntry(getMintSpaceAppStoreCatalog(t), product.appId)?.name || catalogName;
  }, [product, catalogName, t]);

  // Every app buying it unlocks — listed when that's more than one, so a bundle says what's in it.
  const includedApps = useMemo(() => {
    const catalog = getMintSpaceAppStoreCatalog(t);
    return (data?.includedApps || []).map((app, i) => {
      const entry = resolveAppCatalogEntry(catalog, app.appId);
      return {
        key: app.instanceId || `${app.appId}-${i}`,
        name: app.name || entry?.name || app.appId,
        icon: entry?.icon || null,
        color: entry?.color || "",
        navId: app.instanceId ? `instance-${app.instanceId}` : null,
      };
    });
  }, [data?.includedApps, t]);

  const isMember =
    membership?.isOwner || membership?.isMember || membership?.isModerator;
  const ownsProduct = !!membership?.hasProductAccess;
  const [recordingDownloadBusy, setRecordingDownloadBusy] = useState(false);

  const downloadLinkedRecording = async () => {
    if (!spaceId || !productId) return;
    setRecordingDownloadBusy(true);
    try {
      const blob = await downloadProductConsultingRecording(spaceId, productId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "session-recording.mp4";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setToast(err.response?.data?.message || err.message || "Download failed");
    } finally {
      setRecordingDownloadBusy(false);
    }
  };

  const joinModalMintSpace = useMemo(() => {
    if (mintSpaceProp?._id) return mintSpaceProp;
    if (!space?._id) return null;
    return {
      _id: space._id,
      name: space.name,
      questions: space.questions || [],
    };
  }, [mintSpaceProp, space]);

  const handleJoinSuccess = useCallback(async () => {
    const mintSpaceId = mintSpaceProp?._id || space?._id;
    window.dispatchEvent(
      new CustomEvent("mintSpaceJoined", { detail: { mintSpaceId } })
    );
    await loadProduct({ silent: true });
    onJoinSuccess?.();
  }, [loadProduct, onJoinSuccess, mintSpaceProp?._id, space?._id]);

  /**
   * Take the buyer to what they bought.
   *
   * Inside the mint space the shell switches to the app itself. On the standalone page
   * we navigate there instead — straight to the app's own tab, which is addressed by
   * its installed instance, so that's looked up from the space first.
   */
  const openLinkedApp = useCallback(async () => {
    // Which install of the app this product was created on — an app can be added to a
    // mint space more than once, and the buyer bought one of them, not all.
    const boughtInstanceId = String(product?.appInstanceId || "").trim();
    const pid = String(product?._id || productId || "");

    if (onOpenApp) {
      if (product?.appId) await onOpenApp(product.appId, boughtInstanceId, pid);
      return;
    }
    let target = `/mint-space/${spaceId}`;
    if (product?.appId) {
      try {
        const res = await getMintSpaceById(spaceId);
        const apps = normalizeInstalledApps(res?.data?.installedApps, res?.data);
        const wanted = normalizeInstalledAppId(product.appId);
        const instance =
          (boughtInstanceId &&
            apps.find((i) => String(i.instanceId) === boughtInstanceId)) ||
          apps.find((i) => normalizeInstalledAppId(i.appId) === wanted);
        if (instance) {
          const nav = instanceNavId(instance);
          const qs = new URLSearchParams({ nav });
          if (wanted === CONSULTING_APP_ID) qs.set("consultingTab", "sessions");
          target += `?${qs.toString()}`;
        }
      } catch {
        // Still land them in the space; the app is in its sidebar.
      }
    }
    navigate(target);
  }, [onOpenApp, product?._id, product?.appId, product?.appInstanceId, productId, spaceId, navigate]);

  const {
    start: startCheckout,
    starting: checkoutStarting,
    order: checkoutOrder,
    close: closeCheckout,
    handlePaid: onCheckoutPaid,
  } = useCheckout({
    onError: (message, code) => {
      // The code stopped working between applying it and paying — used up, say.
      const promoReason = promoErrorText(t, "buyer", code, "");
      if (promoReason) {
        setAppliedPromo(null);
        setPromoOpen(true);
        setPromoError(promoReason);
      }
      setToast(promoReason || message);
    },
    // The checkout has closed itself. Fulfilment already granted access server-side,
    // so refresh in place to show it.
    onPaid: (result) => {
      // Spent: the product is theirs now, so its price no longer needs showing.
      setAppliedPromo(null);
      setToast(result?.trial ? t("payment.trialStarted") : t("payment.paidAccessGranted"));
      handleJoinSuccess();
    },
  });

  const handleJoinClick = async () => {
    if (!isAuthenticated) {
      const returnPath =
        embedded && spaceId && productId
          ? `/mint-space/${spaceId}?nav=product-${productId}`
          : window.location.pathname + window.location.search;
      openAuthModal({ redirectTo: returnPath });
      return;
    }

    if (ownsProduct) {
      openLinkedApp();
      return;
    }

    if (membership?.hasPendingRequest) {
      setToast(
        t("mintSpace.joinRequestSubmitted") ||
          "Your join request has been submitted. The mint space owner will review your request."
      );
      return;
    }

    const isFreeProduct = isProductFreeForJoin(product);

    if (!isFreeProduct) {
      const questions = space?.questions || [];
      if (!isMember) {
        if (questions.length > 0) {
          setShowJoinModal(true);
          return;
        }
        setJoining(true);
        try {
          const result = await joinMintSpace(spaceId);
          if (!result.success) return;
          setToast(t("mintSpace.successfullyJoined") || "Successfully joined mint space!");
          await handleJoinSuccess();
        } catch (err) {
          setToast(
            err?.message ||
              t("mintSpace.errorJoiningMintSpace") ||
              "Error joining mint space"
          );
          return;
        } finally {
          setJoining(false);
        }
      }
      // No method is sent: the buyer chooses on Keepz's page, which is also the only
      // place Apple Pay and Google Pay are offered. A subscription is the exception —
      // Keepz insists on Credo for a plan order — and the server handles that itself.
      await startCheckout(spaceId, product._id || productId, undefined, appliedPromo?.code);
      return;
    }

    const questions = space?.questions || [];
    if (questions.length > 0) {
      setShowJoinModal(true);
      return;
    }

    setJoining(true);
    try {
      const result = await joinMintSpace(spaceId);
      if (result.success) {
        setToast(t("mintSpace.successfullyJoined") || "Successfully joined mint space!");
        await handleJoinSuccess();
        openLinkedApp();
      }
    } catch (err) {
      setToast(
        err?.message ||
          t("mintSpace.errorJoiningMintSpace") ||
          "Error joining mint space"
      );
    } finally {
      setJoining(false);
    }
  };

  /** Ask the server what the product costs with this code — the same checks checkout makes. */
  const applyPromo = async (e) => {
    e?.preventDefault();
    const code = promoInput.trim();
    if (!code || promoBusy || !product) return;
    setPromoBusy(true);
    setPromoError("");
    try {
      setAppliedPromo(await quotePromoCode(spaceId, product._id || productId, code));
      setPromoOpen(false);
    } catch (err) {
      const body = err?.response?.data;
      setPromoError(promoErrorText(t, "buyer", body?.code, body?.message || t("promoCodes.buyer.failed")));
    } finally {
      setPromoBusy(false);
    }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoInput("");
    setPromoError("");
  };

  // ----- theme tokens -----
  const border = "border-separator-default";
  const card = "border-separator-default bg-bg-raised";
  const muted = "text-label-secondary";
  const subtle = "text-label-secondary";
  const mediaBg = "bg-fill-tertiary";

  const shellBg = "font-ds text-label-primary";
  const shellHeight = "min-h-[16rem]";

  if (loading) {
    return (
      <div className={`flex ${shellHeight} w-full items-center justify-center ${shellBg}`}>
        <FiLoader className="h-7 w-7 animate-spin text-accent-default" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className={`flex ${shellHeight} w-full flex-col items-center justify-center gap-2 px-6 text-center ${shellBg}`}>
        <h1 className="text-title-2">
          {errMsg || t("mintSpace.productNotFound") || "Product not found"}
        </h1>
        <p className={`text-subheadline ${muted}`}>
          {t("mintSpace.productNotFoundHint") ||
            "This product doesn't exist or hasn't been published yet."}
        </p>
        <p className={`mt-1 text-footnote ${muted}`}>
          space: {spaceId || "—"} · product: {productId || "(first)"}
        </p>
      </div>
    );
  }

  const apiOwner = data?.owner;
  const propOwner =
    mintSpaceProp?.owner && typeof mintSpaceProp.owner === "object"
      ? mintSpaceProp.owner
      : null;
  const owner =
    apiOwner?._id || apiOwner?.id
      ? apiOwner
      : propOwner?._id || propOwner?.id
        ? { ...apiOwner, ...propOwner }
        : apiOwner;
  const ownerName = owner?.businessName || owner?.username || "";
  const spaceInitial = (space?.name || "?").charAt(0).toUpperCase();

  const symbol = currencySymbol();
  const isFree = isProductFreeForJoin(product);
  const periodWord =
    product.billingType === "recurring" ? PERIOD_WORD[product.period] || "month" : "";
  const priceText = isFree
    ? t("mintSpace.free") || "Free"
    : `${symbol}${product.price ?? 0}${periodWord ? ` / ${periodWord}` : ""}`;

  // A free trial, which Mintly runs itself — nothing is charged until they subscribe.
  // The server says what starting one would give (0 once they've had the product) and,
  // while they're in one, when it ends. Signed out, the product's own trial is shown:
  // most visitors haven't had it. @see mintly-back getMintSpaceProduct
  const trialDays =
    isFree || ownsProduct
      ? 0
      : membership
        ? Number(membership.trialDays) || 0
        : product.billingType === "recurring"
          ? Number(product.trialDays) || 0
          : 0;
  const trialEndsAt = membership?.trialEndsAt ? new Date(membership.trialEndsAt) : null;
  const trialDate = trialEndsAt ? trialEndsAt.toLocaleDateString() : "";

  // What they'll pay with an accepted promo code — on every payment, for a subscription.
  const promoPrice = appliedPromo && !isFree && !ownsProduct ? appliedPromo.finalPrice : null;
  const promoPriceText =
    promoPrice === 0
      ? t("mintSpace.free") || "Free"
      : promoPrice != null
        ? `${promoMoney(promoPrice)}${periodWord ? ` / ${periodWord}` : ""}`
        : "";

  // A trial's button says so whatever the custom text: pressing it charges nothing.
  const defaultJoinLabel = trialDays
    ? t("mintSpace.startTrial", { days: trialDays })
    : product.buttonText?.trim() ||
      (isFree
        ? t("mintSpace.joinForFree") || "Join for free"
        : promoPrice === 0
          ? t("promoCodes.buyer.claimFree")
          : (t("mintSpace.joinFor") || "Join for {price}").replace(
              "{price}",
              promoPrice != null ? promoMoney(promoPrice) : `${symbol}${product.price ?? 0}`
            ));

  // Owners/moderators manage the listing, so they see the buyer-facing label (their custom
  // button text); only joined members get the "Open {app}" launcher label.
  const isManager = membership?.isOwner || membership?.isModerator;
  // Codes are for buying: not for something already theirs, or the seller's own.
  // Nor for a trial, which is free — a code waits until they subscribe.
  const canUsePromo =
    !isFree && !ownsProduct && !isManager && !membership?.hasPendingRequest && !trialDays;
  const buttonLabel =
    ownsProduct && !isManager
      ? (t("mintSpace.openProductApp") || "Open {app}").replace("{app}", appName || "app")
      : membership?.hasPendingRequest
        ? t("mintSpace.requestSent") || "Request Sent"
        : defaultJoinLabel;

  const busy = joining || checkoutStarting;
  const buttonDisabled = busy || membership?.hasPendingRequest;

  const title = product.name || catalogName || t("mintSpace.productNamePlaceholder") || "Product";
  const visibleFaq = (product.faq || []).filter((f) => f.question?.trim());

  const page = (
    <div className={`${shellHeight} w-full ${shellBg}`}>
      <style>{`
        .product-desc ul { list-style: disc; margin: .25rem 0; padding-left: 1.5rem; }
        .product-desc ol { list-style: decimal; margin: .25rem 0; padding-left: 1.5rem; }
        .product-desc a { color: var(--color-accent-text); text-decoration: underline; }
        .product-desc b, .product-desc strong { font-weight: 700; }
      `}</style>

      {/* Top bar */}
      {!embedded && (
      <Button
        variant="plain"
        size="s"
        icon={<Icon name="arrow-left" size={16} />}
        className="mb-space-24"
        onClick={() => navigate(-1)}
      >
        {space?.name}
      </Button>
      )}

      {/* Body */}
      <div>
        <div className="grid grid-cols-1 gap-space-24 lg:grid-cols-[1fr_340px]">
          {/* LEFT: media + meta + title + description + FAQ */}
          <div className="flex min-w-0 flex-col gap-space-24">
            {/* In-page media (image or video) */}
            <div className="relative aspect-[16/9] w-full overflow-hidden rounded-radius-lg bg-bg-inset">
              {isManager && (
                <ProductAppsFolder
                  spaceId={spaceId}
                  title={title}
                  apps={includedApps.filter((app) => app.navId)}
                  buttonClassName="absolute right-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-radius-xs bg-black/55 px-2.5 py-1.5 text-footnote-semibold text-white backdrop-blur-sm transition hover:bg-black/70"
                />
              )}
              {product.bannerImage ? (
                product.bannerType === "video" ? (
                  <video
                    src={product.bannerImage}
                    className="h-full w-full object-cover"
                    controls
                    playsInline
                  />
                ) : (
                  <img src={product.bannerImage} alt="" className="h-full w-full object-cover" />
                )
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <span className="flex size-[64px] items-center justify-center rounded-full bg-fill-secondary text-label-primary">
                    <Icon name={product.bannerType === "video" ? "play" : "image"} size={28} />
                  </span>
                </div>
              )}
            </div>

            {/* Meta pill: price + creator */}
            <div className="flex items-stretch rounded-[22px] border border-separator-default bg-bg-raised">
              <div className="flex min-w-0 flex-1 items-center justify-center gap-space-8 px-space-16 py-space-12">
                <Icon name="wallet" size={16} className="shrink-0 text-label-secondary" />
                <span className="truncate text-footnote-semibold">{priceText}</span>
              </div>
              <div className="my-space-12 w-px bg-separator-default" />
              <div className="flex min-w-0 flex-1 items-center justify-center px-space-16 py-space-8 text-footnote-semibold">
                <UserProfileLink
                  user={owner}
                  avatarSize="xs"
                  isDark={isDark}
                  nameClassName="font-semibold"
                  fallback={ownerName || "—"}
                />
              </div>
            </div>

            <div className="flex flex-col gap-space-12">
              <h1 className="text-title-3">{title}</h1>
              {product.headline && <p className="text-subheadline text-label-secondary">{product.headline}</p>}
              {product.description?.trim() && (
                <div
                  className="product-desc text-body text-label-secondary"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.description) }}
                />
              )}
            </div>

            {includedApps.length > 1 && (
              <div className="flex flex-col gap-space-8">
                <p className="text-caps-label uppercase text-label-secondary">{t("mintSpace.productIncludes")}</p>
                <div className="flex flex-wrap gap-space-8">
                  {includedApps.map((app, idx) => {
                    return (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-space-8 rounded-radius-sm bg-fill-secondary px-space-8 py-space-4 text-footnote-semibold"
                      >
                        <DsAppIcon app={app.id} size={20} />
                        {app.name}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {ownsProduct && product.linkedConsultingRecordingId && (
              <Button
                variant="tinted"
                size="m"
                className="self-start"
                loading={recordingDownloadBusy}
                icon={<FiDownload className="size-5" />}
                onClick={downloadLinkedRecording}
              >
                {t("consulting.downloadRecording") || "Download session recording"}
              </Button>
            )}

            {/* FAQ */}
            {visibleFaq.length > 0 && (
              <div className="flex flex-col gap-space-12">
                <h2 className="text-title-3">{t("mintSpace.faq") || "FAQ"}</h2>
                <div className="flex flex-col gap-space-12">
                  {visibleFaq.map((f, idx) => (
                    <div key={idx} className="flex flex-col gap-space-4">
                      <p className="text-subheadline-semibold">{f.question}</p>
                      {f.answer?.trim() && <p className="text-subheadline text-label-secondary">{f.answer}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: checkout card */}
          <aside className="flex flex-col gap-space-12 lg:sticky lg:top-[104px] lg:self-start">
            <div className="flex flex-col gap-space-16 rounded-radius-lg bg-bg-raised p-space-16">
              {/* Listing banner — the thumbnail set on the right side during creation */}
              <CardMedia
                src={product.listingBanner}
                isVideo={Boolean(product.listingBanner) && product.listingBannerType === "video"}
                placeholderIcon="bag"
                placeholderLabel={t("home.mediaProduct")}
                className="h-[180px] rounded-radius-md"
              />
              <div className="flex flex-col gap-space-8">
                <h3 className="text-title-2">{title}</h3>
                {!isFree &&
                  (promoPrice != null ? (
                    <div className="flex flex-col">
                      <span className="text-subheadline text-label-secondary line-through">{priceText}</span>
                      <span className="text-large-title text-accent-text">{promoPriceText}</span>
                    </div>
                  ) : (
                    <p className="text-large-title">{priceText}</p>
                  ))}
                {trialDays > 0 && (
                  <p className="inline-flex items-center gap-space-4 text-footnote-semibold text-accent-text">
                    <FiGift className="h-3.5 w-3.5" />
                    {t("mintSpace.trialOffer", { days: trialDays, price: priceText })}
                  </p>
                )}
                {trialEndsAt && !isManager && (
                  <p className="inline-flex items-center gap-space-4 text-footnote-semibold text-accent-text">
                    <FiGift className="h-3.5 w-3.5" />
                    {t("mintSpace.trialRunning", { date: trialDate })}
                  </p>
                )}

                {canUsePromo &&
                  (appliedPromo ? (
                    <div className="mt-2.5 rounded-radius-xs border border-accent-default bg-accent-tinted px-2.5 py-2">
                      <div className="flex items-start justify-between gap-2">
                        <span className="flex min-w-0 items-start gap-1.5 text-caption-1-semibold text-accent-text">
                          <FiTag className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                          <span className="min-w-0 break-words">
                            {t("promoCodes.buyer.applied", {
                              code: appliedPromo.code,
                              discount: discountLabel(appliedPromo, t),
                            })}
                          </span>
                        </span>
                        <button
                          type="button"
                          onClick={removePromo}
                          className={`shrink-0 rounded p-0.5 transition hover:bg-fill-quaternary`}
                          aria-label={t("promoCodes.buyer.remove")}
                          title={t("promoCodes.buyer.remove")}
                        >
                          <FiX className={`h-3.5 w-3.5 ${muted}`} />
                        </button>
                      </div>
                      {appliedPromo.isSubscription && promoPrice > 0 && (
                        <p className={`mt-0.5 text-caption-1 ${muted}`}>{t("promoCodes.buyer.every")}</p>
                      )}
                    </div>
                  ) : promoOpen ? (
                    <form onSubmit={applyPromo} className="mt-2.5">
                      <div className="flex gap-1.5">
                        <input
                          value={promoInput}
                          onChange={(e) => {
                            setPromoInput(e.target.value.toUpperCase());
                            setPromoError("");
                          }}
                          placeholder={t("promoCodes.buyer.placeholder")}
                          aria-label={t("promoCodes.buyer.placeholder")}
                          maxLength={32}
                          autoFocus
                          className={`min-w-0 flex-1 rounded-radius-xs border px-2.5 py-2 font-mono text-caption-1 outline-none transition focus:border-accent-default ${
                            "border-separator-default bg-bg-raised"
                          }`}
                        />
                        <button
                          type="submit"
                          disabled={!promoInput.trim() || promoBusy}
                          className={`inline-flex min-w-[4.5rem] items-center justify-center rounded-radius-xs border px-3 text-caption-1-semibold transition hover:border-accent-default hover:text-accent-text disabled:opacity-50 ${border}`}
                        >
                          {promoBusy ? <FiLoader className="h-3.5 w-3.5 animate-spin" /> : t("promoCodes.buyer.apply")}
                        </button>
                      </div>
                      {promoError && <p className="mt-1.5 text-caption-1 text-status-danger">{promoError}</p>}
                    </form>
                  ) : (
                    <Button
                      variant="plain"
                      size="s"
                      className="self-start"
                      // A code is checked against who's buying, so signing in comes first.
                      onClick={() => (isAuthenticated ? setPromoOpen(true) : handleJoinClick())}
                    >
                      {t("promoCodes.buyer.have")}
                    </Button>
                  ))}
                <Button fullWidth className="mt-space-8" onClick={handleJoinClick} disabled={buttonDisabled} loading={busy}>
                  {busy
                    ? checkoutStarting
                      ? t("mintSpace.redirectingToCheckout") || "Taking you to checkout…"
                      : t("mintSpace.joining") || "Joining…"
                    : buttonLabel}
                </Button>
                {trialEndsAt && !isManager && (
                  <Button variant="gray" fullWidth onClick={() => startCheckout(spaceId, product._id || productId)} disabled={busy}>
                    {t("mintSpace.subscribeNow", { price: priceText })}
                  </Button>
                )}
              </div>
            </div>
            <p className="text-center text-caption-1 text-label-tertiary">{t("mintSpace.poweredBy") || "Powered by Mintly"}</p>
          </aside>
        </div>

        <ProductReviews
          spaceId={spaceId}
          productId={product._id}
          isDark={isDark}
          setToast={setToast}
        />
      </div>

      {/* Keepz checkout — independent of the join-questions modal. */}
      <CheckoutModal
        open={Boolean(checkoutOrder)}
        orderId={checkoutOrder?.orderId}
        checkoutUrl={checkoutOrder?.checkoutUrl}
        amount={checkoutOrder?.amount}
        currency={checkoutOrder?.currency}
        isDark={isDark}
        onClose={closeCheckout}
        onPaid={onCheckoutPaid}
      />

      {showJoinModal && joinModalMintSpace && (
        <JoinRequestModal
          isOpen={showJoinModal}
          setIsOpen={setShowJoinModal}
          mintSpace={joinModalMintSpace}
          isDark={isDark}
          setToast={setToast}
          onSuccess={handleJoinSuccess}
        />
      )}
    </div>
  );

  if (embedded) return page;
  return <PageTransition>{page}</PageTransition>;
};

export default MintSpaceProductDetailsPage;
